import type { Room } from "building-types/rooms/roomTypes";
import type { StairCatalogSettings } from "building-types/settings/stairCatalogSettings";
import type { StairConstruction } from "building-types/stairs/stairConstruction";
import { StairConstructionType } from "building-types/stairs/stairConstruction";
import type {
  GeneratedStairBlock,
  RiserInfo,
  StairBlock,
  StairBlockEdgeHosting,
  StairBlockHosting,
  StairBlockLanding,
  StairBlockNodeLanding,
  StairBlockNodeTable,
  StairBlockRun,
  StairConfig,
  StairSetting,
  StairSystem,
} from "building-types/stairs/stairTypes";
import { StairBlockType } from "building-types/stairs/stairTypesEnum";
import type { FeatureSet } from "common/constants/features";
import type { Table } from "common/data/table";
import { arrayShift } from "common/utils/arrayShift";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import objectFilter from "common/utils/objectFilter";
import { getStairCatalog } from "doc/elements/stairSystem/defaultStairCatalog";
import {
  defaultAssembledStairConfig,
  defaultStairConfig,
  defaultStairSettings,
} from "doc/elements/stairSystem/stairConstants";
import { DIST_EPSILON } from "geom/core";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2Shift,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

export enum LastRiser {
  ON_STAIR,
  NOT_ON_STAIR,
}

export function resolveRisers(
  stairHeight: number,
  desiredRiserHeight: number,
  maxRiserHeight: number,
  lastRiser: LastRiser = LastRiser.ON_STAIR,
): RiserInfo {
  const rawNumRisers = stairHeight / desiredRiserHeight;

  let numRiserFloor = Math.floor(rawNumRisers);

  const riserHeightFloor = stairHeight / numRiserFloor;

  if (lastRiser === LastRiser.NOT_ON_STAIR) {
    numRiserFloor--;
  }

  if (riserHeightFloor <= maxRiserHeight) {
    return {
      riserCount: numRiserFloor,
      riserHeightActual: riserHeightFloor,
    };
  }

  let numRiserCeiling = Math.ceil(rawNumRisers);

  const riserHeightCeiling = stairHeight / numRiserCeiling;

  if (lastRiser === LastRiser.NOT_ON_STAIR) {
    numRiserCeiling--;
  }

  return {
    riserCount: numRiserCeiling,
    riserHeightActual: riserHeightCeiling,
  };
}

interface TreadInfo {
  readonly treadCount: number;
  readonly treadDepthActual: number;
}

export function resolveTreads(
  length: number,
  desiredTreadDepth: number,
  minTreadDepth: number,
  maxTreadDepth: number,
  epsilon: number = DIST_EPSILON,
): TreadInfo {
  const rawNumTreads = length / desiredTreadDepth;

  // first try to fit one more tread in exact space
  const numTreadCeiling = Math.ceil(rawNumTreads);
  const treadDepthCeiling = length / numTreadCeiling;
  if (treadDepthCeiling > minTreadDepth || Math.abs(treadDepthCeiling - minTreadDepth) < epsilon) {
    return {
      treadCount: numTreadCeiling,
      treadDepthActual: treadDepthCeiling,
    };
  }

  // then try to fit one less in exact space
  const numTreadFloor = Math.floor(rawNumTreads);
  const treadDepthFloor = length / numTreadFloor;
  if (treadDepthFloor < maxTreadDepth || Math.abs(treadDepthFloor - maxTreadDepth) < epsilon) {
    return {
      treadCount: numTreadFloor,
      treadDepthActual: treadDepthFloor,
    };
  }

  // likely if min and max are the same as the desired (meaning no room for flexing), then treads will be exactly
  //    desired tread depth with room left-over.
  return {
    treadCount: numTreadFloor,
    treadDepthActual: desiredTreadDepth,
  };
}

export function spatialBlockMirror(block: StairBlock | Room, mirror: boolean, mirrorTranslationX: number) {
  let outline = block.outline;
  let edgeIds = block.edgeIds;
  if (mirror) {
    outline = polyLine2ReflectOverYAxis(polyLine2Copy(outline));
    polyLine2IsClosed(outline) ? polyLine2Shift(polyLine2Reverse(outline), 1) : polyLine2Reverse(outline);
    polyLine2Move(outline, mirrorTranslationX, 0);
    edgeIds = arrayShift(edgeIds.slice().reverse(), 1);
  }
  return { outline, edgeIds };
}

export function unionStairTreadsAndLandingsForPDFPlanView(
  stairBlockNodeTable: StairBlockNodeTable,
  resultBlocks: { [blockId: string]: GeneratedStairBlock },
) {
  const blockIdLookup = makeNodeIndexToResultBlockTable(stairBlockNodeTable, resultBlocks);

  for (let i = 0; i < Object.keys(stairBlockNodeTable).length; i++) {
    const node = stairBlockNodeTable[i];

    if (node.type !== StairBlockType.LANDING) {
      continue;
    }

    const polys: PolyLine2[] = [];
    const landingPolys = resultBlocks[blockIdLookup[i]]?.stairPlanPolys;

    if (!landingPolys || !landingPolys.length) {
      continue;
    }

    assertIsNotNullOrUndefined(landingPolys[0]);
    const { height, outline, overrideOutlineForPDF } = landingPolys[0];
    if (overrideOutlineForPDF) {
      continue;
    }

    polys.push(outline);
    landingPolys[0].overrideOutlineForPDF = true;

    joinLandingPolysAtSameHeightRecursively(node, stairBlockNodeTable, resultBlocks, blockIdLookup, height, polys);

    const polysLength = polys.length;
    if (polysLength === 0) {
      continue;
    }
    if (polysLength === 1) {
      landingPolys[0].outlinesPDF = polys;
      continue;
    }

    const res = polygonUnion(polys.map((x) => [x]));
    landingPolys[0].outlinesPDF.push(...res[0]);
  }
}

function joinLandingPolysAtSameHeightRecursively(
  node: StairBlockNodeLanding,
  stairBlockNodeTable: StairBlockNodeTable,
  resultBlocks: { [blockId: string]: GeneratedStairBlock },
  blockIdLookup: { [index: number]: string },
  height: number,
  polys: Float64Array[],
) {
  const neighborsAbove = node.neighborsAbove;

  for (const aboveIndex of neighborsAbove) {
    const childNode = stairBlockNodeTable[aboveIndex];

    if (childNode.type === StairBlockType.RUN) {
      const blockIdAbove = blockIdLookup[aboveIndex];
      if (blockIdAbove === undefined) {
        continue;
      }
      const blockAbove = resultBlocks[blockIdAbove];
      const runAbovePolys = blockAbove?.stairPlanPolys;
      if (!runAbovePolys || runAbovePolys.length === 0) {
        continue;
      }
      assertIsNotNullOrUndefined(runAbovePolys[0]);
      const { height: runHeight, outline: runOutline, overrideOutlineForPDF } = runAbovePolys[0];
      if (overrideOutlineForPDF) {
        // stop, you've already been here
        continue;
      }
      if (Math.abs(height - runHeight) > DIST_EPSILON) {
        continue;
      }

      polys.push(runOutline);
      runAbovePolys[0].overrideOutlineForPDF = true;
      // runs do not recurse
    } else {
      const landingAbovePolys = resultBlocks[blockIdLookup[aboveIndex]].stairPlanPolys;
      if (landingAbovePolys.length === 0) {
        continue;
      }
      assertIsNotNullOrUndefined(landingAbovePolys[0]);
      const { height: landingHeight, outline: landingOutline, overrideOutlineForPDF } = landingAbovePolys[0];
      if (overrideOutlineForPDF) {
        // stop, you've already been here
        continue;
      }
      if (Math.abs(height - landingHeight) > DIST_EPSILON) {
        continue;
      }
      polys.push(landingOutline);
      landingAbovePolys[0].overrideOutlineForPDF = true;

      joinLandingPolysAtSameHeightRecursively(
        childNode,
        stairBlockNodeTable,
        resultBlocks,
        blockIdLookup,
        height,
        polys,
      );
    }
  }

  const neighborsBelow = node.neighborsBelow;
  for (const belowIndex of neighborsBelow) {
    const childNode = stairBlockNodeTable[belowIndex];

    if (childNode.type === StairBlockType.RUN) {
      const runBelowPolys = resultBlocks[blockIdLookup[belowIndex]].stairPlanPolys;
      if (runBelowPolys.length === 0) {
        continue;
      }
      const runBelowPolysLast = runBelowPolys[runBelowPolys.length - 1];
      assertIsNotNullOrUndefined(runBelowPolysLast);
      const { height: runHeight, outline: runOutline, overrideOutlineForPDF } = runBelowPolysLast;
      if (overrideOutlineForPDF) {
        // stop, you've already been here
        continue;
      }
      if (Math.abs(height - runHeight) > DIST_EPSILON) {
        continue;
      }

      polys.push(runOutline);
      runBelowPolysLast.overrideOutlineForPDF = true;
      // runs do not recurse
    } else {
      const landingBelowPolys = resultBlocks[blockIdLookup[belowIndex]].stairPlanPolys;
      if (landingBelowPolys.length === 0) {
        continue;
      }

      assertIsNotNullOrUndefined(landingBelowPolys[0]);
      const { height: landingHeight, outline: landingOutline, overrideOutlineForPDF } = landingBelowPolys[0];
      if (overrideOutlineForPDF) {
        // stop, you've already been here
        continue;
      }
      if (Math.abs(height - landingHeight) > DIST_EPSILON) {
        continue;
      }
      polys.push(landingOutline);
      landingBelowPolys[0].overrideOutlineForPDF = true;

      joinLandingPolysAtSameHeightRecursively(
        childNode,
        stairBlockNodeTable,
        resultBlocks,
        blockIdLookup,
        height,
        polys,
      );
    }
  }
}

function makeNodeIndexToResultBlockTable(
  stairBlockNodeTable: StairBlockNodeTable,
  resultBlocks: { [blockId: string]: GeneratedStairBlock },
): { [index: number]: string } {
  const nodeIndexToResultBlockIdTable: { [index: number]: string } = {};

  for (const i of Object.keys(stairBlockNodeTable)) {
    const { id } = stairBlockNodeTable[i];

    if (!resultBlocks[id]) {
      continue;
    }

    nodeIndexToResultBlockIdTable[i] = id;
  }

  return nodeIndexToResultBlockIdTable;
}

export function isRun(block: StairBlock): block is StairBlockRun {
  return block.type === StairBlockType.RUN;
}

export function isLanding(block: StairBlock): block is StairBlockLanding {
  return block.type === StairBlockType.LANDING;
}

export function isEdgeHosted(block: StairBlock): boolean {
  switch (block.type) {
    case StairBlockType.RUN:
      return block.hostingUpper?.type === "EDGE" || block.hostingLower?.type === "EDGE";
    case StairBlockType.LANDING:
      return block.hosting?.type === "EDGE";
  }
}

export function isEdgeHosting(hosting?: StairBlockHosting): hosting is StairBlockEdgeHosting {
  return hosting?.type === "EDGE";
}

export function isDatumHosted(block: StairBlock): boolean {
  switch (block.type) {
    case StairBlockType.RUN:
      return block.hostingUpper?.type === "DATUM" || block.hostingLower?.type === "DATUM";
    case StairBlockType.LANDING:
      return block.hosting?.type === "DATUM";
  }
}

export function resolveStairSystem(
  stairCatalogSettings: StairCatalogSettings,
  stairSystem: StairSystem,
  defaultSettings: StairSetting = defaultStairSettings,
  stairCatalog?: Table<StairSetting>,
  enabledFeatures?: FeatureSet,
): StairSystem {
  const catalog = getStairCatalog(stairCatalogSettings, stairCatalog, enabledFeatures);
  return resolveStairSystemWithCatalog(catalog, stairSystem, defaultSettings);
}

export function resolveStairSystemWithCatalog(
  catalog: Table<StairSetting>,
  stairSystem: StairSystem,
  defaultSettings: StairSetting = defaultStairSettings,
): StairSystem {
  const catalogSettings = stairSystem.stairSettingId ? catalog[stairSystem.stairSettingId] : undefined;
  return {
    ...defaultSettings,
    ...catalogSettings,
    ...stairSystem,
    config: resolveStairConfig(catalogSettings, stairSystem),
  };
}

/**
 * Drops explicitly-`undefined` keys before a merge spread.
 */
function objectFilterDefined<T extends object>(source: T | undefined): Partial<T> {
  return objectFilter(source ?? {}, (value) => value !== undefined) as Partial<T>;
}

function resolveStairConfig(
  catalogSettings: StairSetting | undefined,
  stairSystem: StairSystem,
  defaultConfig: StairConfig = defaultStairConfig,
): StairConfig {
  return {
    ...defaultConfig,
    ...objectFilterDefined(catalogSettings?.config),
    ...objectFilterDefined(stairSystem.config),
    construction: resolveStairConstruction(
      defaultConfig.construction,
      catalogSettings?.config?.construction,
      stairSystem.config?.construction,
    ),
  };
}

function resolveStairConstruction(
  defaultConstruction: StairConstruction | undefined,
  catalogConstruction: StairConstruction | undefined,
  ctxConstruction: StairConstruction | undefined,
): StairConstruction {
  let resolved = {
    ...defaultConstruction,
    ...objectFilterDefined(catalogConstruction),
    ...objectFilterDefined(ctxConstruction),
  } as StairConstruction;
  if (resolved?.type === StairConstructionType.ASSEMBLED) {
    resolved = {
      ...defaultAssembledStairConfig.construction,
      ...resolved,
    };
  }
  return resolved;
}

export function resolveStairBlock(
  stairSystem: StairSystem,
  stairBlock: StairBlock,
  defaultConfig: StairConfig = defaultStairConfig,
): StairBlock {
  if (!stairSystem) {
    return stairBlock;
  }
  switch (stairBlock.type) {
    case StairBlockType.RUN:
      return {
        ...stairBlock,
        construction: resolveStairConstruction(
          defaultConfig.construction,
          stairSystem.config.construction,
          stairBlock.construction,
        ),
      };
    default:
      break;
  }
  return stairBlock;
}
